import * as THREE from "three";
import { TEX_W } from "./ply";

const vert = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D uData;
uniform vec2 uViewport;
uniform float uFocal;
uniform vec3 uSunView;
uniform vec3 uSunCol;
uniform float uLight;
attribute float splatIndex;
varying vec4 vColor;
varying vec2 vPos;
ivec2 tc(int i) { return ivec2(i % ${TEX_W}, i / ${TEX_W}); }
void main() {
  int b = int(splatIndex) * 4;
  vec4 t0 = texelFetch(uData, tc(b), 0);
  vec4 t1 = texelFetch(uData, tc(b + 1), 0);
  vec4 t2 = texelFetch(uData, tc(b + 2), 0);
  vec4 t3 = texelFetch(uData, tc(b + 3), 0);
  vec4 cam = modelViewMatrix * vec4(t0.xyz, 1.0);
  vec4 clip = projectionMatrix * cam;
  float lim = 1.3 * clip.w;
  if (cam.z > -0.02 || abs(clip.x) > lim || abs(clip.y) > lim) { gl_Position = vec4(0.0, 0.0, 2.0, 1.0); return; }
  mat3 Vrk = mat3(t2.x, t2.y, t2.z, t2.y, t2.w, t3.x, t2.z, t3.x, t3.y);
  float z = cam.z;
  mat3 J = mat3(uFocal / -z, 0.0, 0.0, 0.0, uFocal / -z, 0.0, uFocal * cam.x / (z * z), uFocal * cam.y / (z * z), 0.0);
  mat3 W = mat3(modelViewMatrix);
  mat3 T = J * W;
  mat3 cov = T * Vrk * transpose(T);
  float a = cov[0][0] + 0.3;
  float d = cov[1][1] + 0.3;
  float c = cov[1][0];
  float mid = 0.5 * (a + d);
  float rad = length(vec2(0.5 * (a - d), c));
  float l1 = mid + rad;
  float l2 = max(mid - rad, 0.1);
  vec2 v1 = abs(c) < 1e-7 ? (a >= d ? vec2(1.0, 0.0) : vec2(0.0, 1.0)) : normalize(vec2(c, l1 - a));
  vec2 v2 = vec2(-v1.y, v1.x);
  vec2 off = position.x * min(sqrt(l1), 600.0) * v1 + position.y * min(sqrt(l2), 600.0) * v2;
  vPos = position.xy;
  gl_Position = vec4(clip.xy / clip.w + off * 2.0 / uViewport, 0.0, 1.0);

  vec3 n = vec3(t0.w, t3.z, t3.w);
  float fl = clamp(length(n), 0.0, 1.0);
  vec3 nv = fl > 0.001 ? normalize(mat3(modelViewMatrix) * n) : vec3(0.0, 0.0, 1.0);
  vec3 vd = normalize(-cam.xyz);
  float face = abs(dot(nv, uSunView));
  float spec = pow(abs(dot(nv, normalize(uSunView + vd))), 28.0);
  float rim = pow(1.0 - abs(dot(nv, vd)), 3.0) * face;
  vec3 col = t1.rgb * (0.85 + 0.75 * face * fl);
  col += uSunCol * (spec * 0.8 + rim * 0.5) * fl * uLight;
  vColor = vec4(col, t1.a);
}`;

const frag = /* glsl */ `
precision highp float;
uniform float uAlpha;
varying vec4 vColor;
varying vec2 vPos;
void main() {
  float A = -0.5 * dot(vPos, vPos);
  if (A < -4.0) discard;
  float B = min(exp(A) * vColor.a * uAlpha, 0.995);
  if (B < 1.0 / 255.0) discard;
  gl_FragColor = vec4(vColor.rgb * B, B);
}`;

export function createSplatMesh(splats) {
  const { count, tex, texHeight, centers } = splats;
  const R = 2.83;
  const geo = new THREE.InstancedBufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array([-R, -R, 0, R, -R, 0, R, R, 0, -R, R, 0]), 3));
  geo.setIndex([0, 1, 2, 0, 2, 3]);
  const order = new Float32Array(count);
  for (let i = 0; i < count; i++) order[i] = i;
  const attr = new THREE.InstancedBufferAttribute(order, 1);
  attr.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute("splatIndex", attr);
  geo.instanceCount = count;
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 2);

  const dataTex = new THREE.DataTexture(tex, TEX_W, texHeight, THREE.RGBAFormat, THREE.FloatType);
  dataTex.needsUpdate = true;
  const material = new THREE.ShaderMaterial({
    vertexShader: vert,
    fragmentShader: frag,
    uniforms: {
      uData: { value: dataTex },
      uViewport: { value: new THREE.Vector2(1, 1) },
      uFocal: { value: 1 },
      uSunView: { value: new THREE.Vector3(0, 0, 1) },
      uSunCol: { value: new THREE.Color(1.0, 0.62, 0.3) },
      uLight: { value: 1.35 },
      uAlpha: { value: 0 },
    },
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
  });
  const mesh = new THREE.Mesh(geo, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;

  const depth = new Float32Array(count);
  const keys = new Uint16Array(count);
  const counts = new Uint32Array(65536);
  let last = null;

  const sort = (mv) => {
    const e = mv.elements;
    const a = e[2], b = e[6], c = e[10];
    if (last && Math.abs(a - last[0]) + Math.abs(b - last[1]) + Math.abs(c - last[2]) < 0.0015) return;
    last = [a, b, c];
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < count; i++) {
      const v = a * centers[i * 3] + b * centers[i * 3 + 1] + c * centers[i * 3 + 2];
      depth[i] = v;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    const s = 65535 / (hi - lo || 1);
    counts.fill(0);
    for (let i = 0; i < count; i++) { const k = ((depth[i] - lo) * s) | 0; keys[i] = k; counts[k]++; }
    let sum = 0;
    for (let k = 0; k < 65536; k++) { const t = counts[k]; counts[k] = sum; sum += t; }
    for (let i = 0; i < count; i++) order[counts[keys[i]]++] = i;
    attr.needsUpdate = true;
  };

  return { mesh, material, sort };
}
